/**
 * §3.6 Checkout — precontract information technical shell (SHIPPING OFF).
 * Slots: main characteristics + seller identity + total/fulfillment terms.
 * BAN invent LEGAL prose — i18n keys / technical placeholders only.
 * NOT shippingLightEnabled-gated. Wave0 CMS ≠ this surface.
 */

import { useTranslation } from 'react-i18next';
import type { PrecontractShellSlots } from '../presenters/buildPrecontractShellSlots.js';

export type PrecontractInfoShellProps = {
  readonly slots: PrecontractShellSlots;
};

function SlotRow({
  testId,
  labelKey,
  value,
  placeholderKey,
  t,
}: {
  readonly testId: string;
  readonly labelKey: string;
  readonly value: string | null;
  readonly placeholderKey: string;
  readonly t: (key: string) => string;
}): JSX.Element {
  const resolved = value != null && value.trim().length > 0 ? value : t(placeholderKey);
  return (
    <div data-testid={testId} className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-[var(--color-on-surface-muted)]">
        {t(labelKey)}
      </dt>
      <dd className="text-sm text-[var(--color-on-surface)]">{resolved}</dd>
    </div>
  );
}

export function PrecontractInfoShell({ slots }: PrecontractInfoShellProps): JSX.Element {
  const { t } = useTranslation('customer');

  return (
    <section
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3"
      data-testid="customer-checkout-precontract-shell"
      aria-labelledby="customer-checkout-precontract-title"
    >
      <h2
        id="customer-checkout-precontract-title"
        className="text-sm font-semibold text-[var(--color-on-surface)]"
      >
        {t('checkout.precontract.sectionTitle')}
      </h2>
      <dl className="mt-3 flex flex-col gap-3">
        <SlotRow
          testId="customer-checkout-precontract-characteristics"
          labelKey="checkout.precontract.mainCharacteristics.label"
          value={slots.mainCharacteristics.value}
          placeholderKey="checkout.precontract.mainCharacteristics.placeholder"
          t={t}
        />
        <SlotRow
          testId="customer-checkout-precontract-seller"
          labelKey="checkout.precontract.sellerIdentity.label"
          value={slots.sellerIdentity.value}
          placeholderKey="checkout.precontract.sellerIdentity.placeholder"
          t={t}
        />
        <SlotRow
          testId="customer-checkout-precontract-total-fulfillment"
          labelKey="checkout.precontract.totalFulfillment.label"
          value={slots.totalAndFulfillmentTerms.value}
          placeholderKey="checkout.precontract.totalFulfillment.placeholder"
          t={t}
        />
      </dl>
    </section>
  );
}
